import { CHALLENGE_MODIFIERS } from "./config.js";
import { SeededRandom, clamp } from "./core.js";

const keyOf = (x, y) => `${x},${y}`;
const neighbors = (x, y) => [
  [x + 1, y],
  [x - 1, y],
  [x, y + 1],
  [x, y - 1],
];

export class NavigationGrid {
  constructor(width, height, walkable = []) {
    this.width = width;
    this.height = height;
    this.walkable = new Set(walkable.map((cell) => keyOf(cell.x, cell.y)));
    this.revision = 1;
    this.cache = new Map();
    this.cacheHits = 0;
    this.searches = 0;
  }
  isWalkable(x, y) {
    return (
      x >= 0 &&
      y >= 0 &&
      x < this.width &&
      y < this.height &&
      this.walkable.has(keyOf(x, y))
    );
  }
  setBlocked(x, y, blocked) {
    const key = keyOf(x, y);
    const changed = blocked
      ? this.walkable.delete(key)
      : !this.walkable.has(key);
    if (!blocked) this.walkable.add(key);
    if (!changed) return false;
    this.revision += 1;
    this.cache.clear();
    return true;
  }
  nearestReachable(point, radius = 5) {
    const origin = {
      x: clamp(Math.round(point.x), 0, this.width - 1),
      y: clamp(Math.round(point.y), 0, this.height - 1),
    };
    if (this.isWalkable(origin.x, origin.y)) return origin;
    for (let distance = 1; distance <= radius; distance += 1) {
      const candidates = [];
      for (let offset = -distance; offset <= distance; offset += 1) {
        candidates.push(
          { x: origin.x + offset, y: origin.y - distance },
          { x: origin.x + offset, y: origin.y + distance },
          { x: origin.x - distance, y: origin.y + offset },
          { x: origin.x + distance, y: origin.y + offset },
        );
      }
      const reachable = candidates
        .filter((cell) => this.isWalkable(cell.x, cell.y))
        .sort(
          (a, b) =>
            Math.hypot(a.x - point.x, a.y - point.y) -
              Math.hypot(b.x - point.x, b.y - point.y) ||
            a.y - b.y ||
            a.x - b.x,
        )[0];
      if (reachable) return reachable;
    }
    return null;
  }
  hasLineOfSight(startInput, endInput) {
    let x = Math.round(startInput.x),
      y = Math.round(startInput.y);
    const endX = Math.round(endInput.x),
      endY = Math.round(endInput.y),
      dx = Math.abs(endX - x),
      dy = Math.abs(endY - y),
      stepX = x < endX ? 1 : -1,
      stepY = y < endY ? 1 : -1;
    let error = dx - dy;
    for (let visited = 0; visited <= this.width + this.height; visited += 1) {
      if (!this.isWalkable(x, y)) return false;
      if (x === endX && y === endY) return true;
      const doubled = error * 2;
      if (doubled > -dy) {
        error -= dy;
        x += stepX;
      }
      if (doubled < dx) {
        error += dx;
        y += stepY;
      }
    }
    return false;
  }
  findPath(startInput, goalInput, maxNodes = 2400) {
    const start = this.nearestReachable(startInput);
    const goal = this.nearestReachable(goalInput);
    if (!start || !goal) return [];
    const cacheKey = `${this.revision}:${keyOf(start.x, start.y)}>${keyOf(goal.x, goal.y)}`;
    if (this.cache.has(cacheKey)) {
      this.cacheHits += 1;
      return this.cache.get(cacheKey).map((entry) => ({ ...entry }));
    }
    this.searches += 1;
    const open = [start];
    const cameFrom = new Map();
    const g = new Map([[keyOf(start.x, start.y), 0]]);
    const closed = new Set();
    let visited = 0;
    while (open.length && visited < maxNodes) {
      visited += 1;
      open.sort(
        (a, b) =>
          g.get(keyOf(a.x, a.y)) +
            Math.abs(goal.x - a.x) +
            Math.abs(goal.y - a.y) -
            (g.get(keyOf(b.x, b.y)) +
              Math.abs(goal.x - b.x) +
              Math.abs(goal.y - b.y)) ||
          a.y - b.y ||
          a.x - b.x,
      );
      const current = open.shift();
      const currentKey = keyOf(current.x, current.y);
      if (current.x === goal.x && current.y === goal.y) {
        const path = [current];
        let cursor = currentKey;
        while (cameFrom.has(cursor)) {
          const previous = cameFrom.get(cursor);
          path.push(previous);
          cursor = keyOf(previous.x, previous.y);
        }
        path.reverse();
        this.cache.set(cacheKey, path);
        if (this.cache.size > 60)
          this.cache.delete(this.cache.keys().next().value);
        return path.map((entry) => ({ ...entry }));
      }
      closed.add(currentKey);
      for (const [x, y] of neighbors(current.x, current.y)) {
        const nextKey = keyOf(x, y);
        if (!this.isWalkable(x, y) || closed.has(nextKey)) continue;
        const cost = (g.get(currentKey) ?? Infinity) + 1;
        if (cost >= (g.get(nextKey) ?? Infinity)) continue;
        cameFrom.set(nextKey, current);
        g.set(nextKey, cost);
        if (!open.some((entry) => entry.x === x && entry.y === y))
          open.push({ x, y });
      }
    }
    return [];
  }
}

function roomOverlaps(room, rooms, margin = 2) {
  return rooms.some(
    (other) =>
      room.x < other.x + other.w + margin &&
      room.x + room.w + margin > other.x &&
      room.y < other.y + other.h + margin &&
      room.y + room.h + margin > other.y,
  );
}

function carveRoom(tiles, room) {
  for (let y = room.y; y < room.y + room.h; y += 1)
    for (let x = room.x; x < room.x + room.w; x += 1) tiles[y][x] = 1;
}

function carveCorridor(tiles, from, to, horizontalFirst) {
  let x = from.x,
    y = from.y;
  const cells = [];
  const stepX = () => {
    while (x !== to.x) {
      x += Math.sign(to.x - x);
      tiles[y][x] = 1;
      cells.push({ x, y });
    }
  };
  const stepY = () => {
    while (y !== to.y) {
      y += Math.sign(to.y - y);
      tiles[y][x] = 1;
      cells.push({ x, y });
    }
  };
  horizontalFirst ? (stepX(), stepY()) : (stepY(), stepX());
  return cells;
}

const center = (room) => ({
  x: Math.floor(room.x + room.w / 2),
  y: Math.floor(room.y + room.h / 2),
});

function generateAttempt(seed, options, attempt) {
  const rng = new SeededRandom(`${seed}:${attempt}`);
  const width = options.width ?? 58;
  const height = options.height ?? 46;
  const tiles = Array.from({ length: height }, () => Array(width).fill(0));
  const rooms = [];
  const roomTarget = clamp(options.rooms ?? 12, 8, 18);
  for (let tries = 0; tries < 180 && rooms.length < roomTarget; tries += 1) {
    const room = {
      id: `room-${rooms.length}`,
      x: rng.int(2, width - 12),
      y: rng.int(2, height - 11),
      w: rng.int(6, 10),
      h: rng.int(6, 9),
      type: "combat",
    };
    if (roomOverlaps(room, rooms)) continue;
    rooms.push(room);
    carveRoom(tiles, room);
  }
  if (rooms.length < 8) return null;
  rooms.sort((a, b) => a.x - b.x || a.y - b.y);
  const corridors = [];
  const primaryCorridors = [];
  for (let index = 1; index < rooms.length; index += 1) {
    const carved = carveCorridor(
      tiles,
      center(rooms[index - 1]),
      center(rooms[index]),
      rng.next() < 0.5,
    );
    primaryCorridors.push(carved);
    corridors.push(...carved);
  }
  for (let index = 0; index < Math.floor(rooms.length / 3); index += 1) {
    const first = rng.int(0, rooms.length - 2);
    const second = rng.int(first + 1, rooms.length - 1);
    corridors.push(
      ...carveCorridor(
        tiles,
        center(rooms[first]),
        center(rooms[second]),
        rng.next() < 0.5,
      ),
    );
  }
  rooms[0].type = "entrance";
  rooms[rooms.length - 1].type = "boss";
  rooms[rooms.length - 2].type = "checkpoint";
  if (rooms.length > 8) rooms[2].type = "treasure";
  if (rooms.length > 10) rooms[4].type = "shrine";
  if (rooms.length > 11) rooms[6].type = "merchant";
  const secretIndex = Math.max(3, Math.floor(rooms.length / 2));
  rooms[secretIndex].type = "secret";
  const lockRoomIndex = Math.max(5, rooms.length - 3);
  rooms[lockRoomIndex].locked = "sigil-key";
  const keyRoomIndex = Math.max(1, Math.floor(lockRoomIndex / 2));
  rooms[keyRoomIndex].key = "sigil-key";
  const lockCorridor = primaryCorridors[lockRoomIndex - 1];
  const lockCell =
    lockCorridor[Math.max(0, Math.floor(lockCorridor.length / 2))] ??
    center(rooms[lockRoomIndex]);
  const traps = rooms
    .filter((room) => room.type === "combat")
    .slice(0, 3)
    .map((room, index) => ({
      id: `trap-${index}`,
      type: rng.pick(["spikes", "cinder", "frost-rune"]),
      ...center(room),
    }));
  const spawns = rooms
    .filter((room) => room.type === "combat")
    .flatMap((room, roomIndex) =>
      Array.from({ length: rng.int(1, 3) }, (_, index) => ({
        id: `spawn-${roomIndex}-${index}`,
        roomId: room.id,
        x: room.x + 2 + ((index * 2) % Math.max(2, room.w - 3)),
        y: room.y + 2 + Math.floor(index / 2) * 2,
      })),
    );
  return {
    schemaVersion: 1,
    seed: String(seed),
    attempt,
    width,
    height,
    tiles,
    rooms,
    corridors,
    entrance: center(rooms[0]),
    exit: center(rooms[rooms.length - 1]),
    boss: center(rooms[rooms.length - 1]),
    checkpoint: center(rooms[rooms.length - 2]),
    key: { id: "sigil-key", ...center(rooms[keyRoomIndex]) },
    lock: {
      id: "sigil-door",
      keyId: "sigil-key",
      ...lockCell,
      roomId: rooms[lockRoomIndex].id,
    },
    secret: center(rooms[secretIndex]),
    traps,
    spawns,
    treasure: center(rooms.find((room) => room.type === "treasure")),
    fallback: false,
  };
}

export function fallbackDungeon(seed, options = {}) {
  const width = options.width ?? 58,
    height = options.height ?? 46;
  const tiles = Array.from({ length: height }, () => Array(width).fill(0));
  const rooms = [
    { id: "room-0", x: 3, y: 17, w: 9, h: 9, type: "entrance" },
    {
      id: "room-1",
      x: 17,
      y: 8,
      w: 10,
      h: 9,
      type: "treasure",
      key: "sigil-key",
    },
    { id: "room-2", x: 17, y: 28, w: 10, h: 9, type: "secret" },
    {
      id: "room-3",
      x: 33,
      y: 17,
      w: 9,
      h: 9,
      type: "checkpoint",
      locked: "sigil-key",
    },
    { id: "room-4", x: 47, y: 15, w: 9, h: 13, type: "boss" },
  ];
  rooms.forEach((room) => carveRoom(tiles, room));
  const links = [
    [0, 1],
    [0, 2],
    [1, 3],
    [2, 3],
    [3, 4],
  ];
  const corridors = links.flatMap(([a, b]) =>
    carveCorridor(tiles, center(rooms[a]), center(rooms[b]), true),
  );
  return {
    schemaVersion: 1,
    seed: String(seed),
    attempt: -1,
    width,
    height,
    tiles,
    rooms,
    corridors,
    entrance: center(rooms[0]),
    exit: center(rooms[4]),
    boss: center(rooms[4]),
    checkpoint: center(rooms[3]),
    key: { id: "sigil-key", ...center(rooms[1]) },
    lock: {
      id: "sigil-door",
      keyId: "sigil-key",
      ...center(rooms[3]),
      roomId: "room-3",
    },
    secret: center(rooms[2]),
    traps: [{ id: "trap-0", type: "spikes", x: 37, y: 21 }],
    spawns: [{ id: "spawn-0", roomId: "room-3", x: 36, y: 20 }],
    treasure: center(rooms[1]),
    fallback: true,
  };
}

export function dungeonWalkableCells(dungeon, ignoreLock = true) {
  const cells = [];
  for (let y = 0; y < dungeon.height; y += 1)
    for (let x = 0; x < dungeon.width; x += 1)
      if (
        dungeon.tiles[y][x] === 1 &&
        (ignoreLock || x !== dungeon.lock.x || y !== dungeon.lock.y)
      )
        cells.push({ x, y });
  return cells;
}

export function validateDungeon(dungeon) {
  const errors = [];
  if (!dungeon || !Array.isArray(dungeon.tiles) || !dungeon.rooms?.length)
    return { valid: false, errors: ["Malformed dungeon."] };
  if (
    !Number.isInteger(dungeon.width) ||
    !Number.isInteger(dungeon.height) ||
    dungeon.width < 20 ||
    dungeon.height < 20 ||
    dungeon.tiles.length !== dungeon.height ||
    dungeon.tiles.some(
      (row) => !Array.isArray(row) || row.length !== dungeon.width,
    )
  )
    return { valid: false, errors: ["Invalid dungeon dimensions."] };
  const allGrid = new NavigationGrid(
    dungeon.width,
    dungeon.height,
    dungeonWalkableCells(dungeon, true),
  );
  const lockedGrid = new NavigationGrid(
    dungeon.width,
    dungeon.height,
    dungeonWalkableCells(dungeon, false),
  );
  if (!allGrid.findPath(dungeon.entrance, dungeon.exit).length)
    errors.push("Exit is unreachable.");
  if (!allGrid.findPath(dungeon.entrance, dungeon.boss).length)
    errors.push("Boss is unreachable.");
  if (!allGrid.findPath(dungeon.entrance, dungeon.checkpoint).length)
    errors.push("Checkpoint is unreachable.");
  if (!lockedGrid.findPath(dungeon.entrance, dungeon.key).length)
    errors.push("Key is behind its own lock.");
  const onFloor = (point) => point && dungeon.tiles[point.y]?.[point.x] === 1;
  if (!onFloor(dungeon.entrance)) errors.push("Entrance is not walkable.");
  if (!onFloor(dungeon.exit)) errors.push("Exit is not walkable.");
  if (!onFloor(dungeon.boss)) errors.push("Boss spawn is invalid.");
  if (!onFloor(dungeon.checkpoint)) errors.push("Checkpoint is invalid.");
  if (!onFloor(dungeon.key)) errors.push("Key spawn is invalid.");
  if (!onFloor(dungeon.lock)) errors.push("Door position is invalid.");
  if (!onFloor(dungeon.secret)) errors.push("Secret room is invalid.");
  for (const spawn of dungeon.spawns)
    if (!onFloor(spawn)) errors.push(`Invalid enemy spawn ${spawn.id}.`);
  const bossRoom = dungeon.rooms.find((room) => room.type === "boss");
  if (!bossRoom || bossRoom.w * bossRoom.h < 60)
    errors.push("Boss arena is too small.");
  if (!dungeon.rooms.some((room) => room.type === "secret"))
    errors.push("No secret room.");
  const keyRoomIndex = dungeon.rooms.findIndex(
    (room) => room.key === dungeon.key.id,
  );
  const lockRoomIndex = dungeon.rooms.findIndex(
    (room) =>
      room.id === dungeon.lock.roomId && room.locked === dungeon.lock.keyId,
  );
  if (keyRoomIndex < 0 || lockRoomIndex < 0 || keyRoomIndex >= lockRoomIndex)
    errors.push("Required key is not placed before its lock.");
  return {
    valid: errors.length === 0,
    errors,
    reachableCells: allGrid.walkable.size,
  };
}

export function generateDungeon(seed, options = {}) {
  for (let attempt = 0; attempt < (options.maxAttempts ?? 8); attempt += 1) {
    const dungeon = generateAttempt(seed, options, attempt);
    if (dungeon && validateDungeon(dungeon).valid) return dungeon;
  }
  return fallbackDungeon(seed, options);
}

export function challengeDefinition(seed, tier = 1) {
  const rng = new SeededRandom(`${seed}:tier:${tier}`);
  const count = clamp(1 + Math.floor(tier / 3), 1, 4);
  return {
    seed: String(seed),
    tier: clamp(Math.floor(tier), 1, 30),
    modifiers: rng.shuffle(CHALLENGE_MODIFIERS).slice(0, count),
    timeGoal: Math.max(240, 720 - tier * 14),
    enemyScale: Number((1 + tier * 0.13).toFixed(2)),
    eliteChance: clamp(0.08 + tier * 0.018, 0.08, 0.48),
    reward: { gold: 150 + tier * 45, challengeMarks: 2 + Math.floor(tier / 2) },
  };
}
